/**
 * The BI screens.
 *
 * One deliberate choice runs through all of them: **Delivery Orders are the revenue line, not
 * Sales Orders.** A SO is an intention; a DO is stock that left the building. The gap between the
 * two is the order book, which gets its own panel rather than being quietly folded into "sales".
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';

import { api, money, qty } from '../lib/api';
import { businessDate } from '../lib/time';
import { CHART } from '../lib/chart';
import { useAuth } from '../lib/auth';
import { Card, Table, RangePicker, Loading, Empty, useRange, cell } from '../components/ui';

export function BiOverview() {
  const { range, setRange, params } = useRange();

  const summary = useQuery({
    queryKey: ['bi-summary', params],
    queryFn: () =>
      api.get<{
        byDoctype: Array<{
          doctype: string;
          documents: number;
          amount: string;
          qty: string;
          partners: number;
          skus: number;
        }>;
      }>(`/api/v1/bi/summary?${params}`),
  });

  const trend = useQuery({
    queryKey: ['bi-trend', params],
    queryFn: () =>
      api.get<{ series: Array<Record<string, string | number>> }>(
        `/api/v1/bi/sales/trend?${params}`,
      ),
  });

  const book = useQuery({
    queryKey: ['bi-book'],
    queryFn: () =>
      api.get<{ orderBook: Array<{ doctype: string; lines: number; outstanding_qty: string }> }>(
        '/api/v1/bi/order-book',
      ),
  });

  const by = (t: string) => summary.data?.byDoctype.find((d) => d.doctype === t);

  // Recharts needs one row per bucket with a column per series.
  const chart = (() => {
    const rows = new Map<string, Record<string, number | string>>();
    for (const r of trend.data?.series ?? []) {
      const key = String(r['bucket']);
      const row = rows.get(key) ?? { bucket: key };
      row[String(r['doctype'])] = Number(r['amount'] ?? 0);
      rows.set(key, row);
    }
    return [...rows.values()];
  })();

  return (
    <div className="space-y-6">
      <RangePicker range={range} onChange={setRange} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label="Delivered (DO)"
          value={money(by('DO')?.amount)}
          sub={`${by('DO')?.documents ?? 0} documents`}
        />
        <Kpi
          label="Ordered (SO)"
          value={money(by('SO')?.amount)}
          sub={`${by('SO')?.documents ?? 0} documents`}
        />
        <Kpi
          label="Purchased (PO)"
          value={money(by('PO')?.amount)}
          sub={`${by('PO')?.documents ?? 0} documents`}
        />
        <Kpi
          label="Received (GR)"
          value={money(by('GR')?.amount)}
          sub={`${by('GR')?.documents ?? 0} documents`}
        />
      </div>

      <Card
        title="Sales trend"
        subtitle="SO is what was ordered; DO is what was delivered. The gap is the order book."
      >
        {trend.isLoading ? (
          <Loading />
        ) : chart.length === 0 ? (
          <Empty>No documents in this range.</Empty>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip formatter={(v) => money(v as number)} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="DO"
                  stroke={CHART.primary}
                  strokeWidth={2}
                  dot={false}
                  name="Delivered"
                />
                <Line
                  type="monotone"
                  dataKey="SO"
                  stroke={CHART.light}
                  strokeWidth={2}
                  dot={false}
                  name="Ordered"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card
        title="Order book"
        subtitle="Lines still open — ordered but not yet received or delivered."
      >
        {book.isLoading ? (
          <Loading />
        ) : (
          <Table
            head={['Type', 'Open lines', 'Outstanding qty']}
            rows={(book.data?.orderBook ?? []).map((r) => [
              r.doctype,
              r.lines.toLocaleString(),
              qty(r.outstanding_qty),
            ])}
          />
        )}
      </Card>
    </div>
  );
}

export function BiSales() {
  const { range, setRange, params } = useRange();

  const customers = useQuery({
    queryKey: ['bi-customers', params],
    queryFn: () =>
      api.get<{
        customers: Array<{
          partner_code: string;
          partner_name: string;
          documents: number;
          amount: string;
          qty: string;
          last_order: string;
        }>;
      }>(`/api/v1/bi/sales/customers?${params}&limit=25`),
  });

  const outlets = useQuery({
    queryKey: ['bi-outlets', params],
    queryFn: () =>
      api.get<{
        outlets: Array<{
          location: string;
          amount: string;
          qty: string;
          documents: number;
          skus: number;
          customers: number;
        }>;
      }>(`/api/v1/bi/outlets?${params}`),
  });

  return (
    <div className="space-y-6">
      <RangePicker range={range} onChange={setRange} />

      <Card title="Top customers" subtitle="By delivered value.">
        {customers.isLoading ? (
          <Loading />
        ) : (
          <Table
            head={['Customer', 'Code', 'Delivered', 'Qty', 'Docs', 'Last order']}
            rows={(customers.data?.customers ?? []).map((c) => [
              c.partner_name ?? '—',
              c.partner_code ?? '—',
              money(c.amount),
              qty(c.qty),
              String(c.documents),
              c.last_order ?? '—',
            ])}
          />
        )}
      </Card>

      <Card title="Outlet performance" subtitle="Delivered value by location.">
        {outlets.isLoading ? (
          <Loading />
        ) : (
          <>
            <div className="mb-4 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={(outlets.data?.outlets ?? []).slice(0, 15).map((o) => ({
                    location: o.location,
                    amount: Number(o.amount),
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} />
                  <XAxis
                    dataKey="location"
                    tick={{ fontSize: 10 }}
                    angle={-35}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `${Math.round(v / 1000)}k`}
                  />
                  <Tooltip formatter={(v) => money(v as number)} />
                  <Bar dataKey="amount" fill={CHART.primary} name="Delivered" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <Table
              head={['Location', 'Delivered', 'Qty', 'Docs', 'SKUs', 'Customers']}
              rows={(outlets.data?.outlets ?? []).map((o) => [
                o.location,
                money(o.amount),
                qty(o.qty),
                String(o.documents),
                String(o.skus),
                String(o.customers),
              ])}
            />
          </>
        )}
      </Card>
    </div>
  );
}

export function BiProducts() {
  const { range, setRange, params } = useRange();
  const { can } = useAuth();

  const products = useQuery({
    queryKey: ['bi-products', params],
    queryFn: () =>
      api.get<{
        costVisible: boolean;
        products: Array<{
          itemcode: string;
          description: string;
          qty: string;
          amount: string;
          documents: number;
          customers: number;
          avg_price: string;
          avgCost?: string | null;
        }>;
      }>(`/api/v1/bi/products?${params}&limit=50`),
  });

  const showCost = can('bi.cost.read');

  return (
    <div className="space-y-6">
      <RangePicker range={range} onChange={setRange} />
      <Card
        title="Product performance"
        subtitle={
          showCost
            ? 'Ranked by delivered value. Average cost is from goods received in the same window.'
            : 'Ranked by delivered value.'
        }
      >
        {products.isLoading ? (
          <Loading />
        ) : (
          <Table
            head={[
              'Item',
              'Description',
              'Qty',
              'Delivered',
              'Avg price',
              ...(showCost ? ['Avg cost'] : []),
              'Customers',
            ]}
            rows={(products.data?.products ?? []).map((p) => [
              p.itemcode,
              p.description ?? '—',
              qty(p.qty),
              money(p.amount),
              money(p.avg_price),
              ...(showCost ? [money(p.avgCost ?? null)] : []),
              String(p.customers),
            ])}
          />
        )}
      </Card>
    </div>
  );
}

export function BiPurchasing() {
  const { range, setRange, params } = useRange();

  const suppliers = useQuery({
    queryKey: ['bi-suppliers', params],
    queryFn: () =>
      api.get<{
        suppliers: Array<{
          partner_code: string;
          partner_name: string;
          documents: number;
          amount: string;
          qty: string;
          last_order: string;
        }>;
      }>(`/api/v1/bi/purchasing/suppliers?${params}&limit=25`),
  });

  const lead = useQuery({
    queryKey: ['bi-lead', params],
    queryFn: () =>
      api.get<{
        suppliers: Array<{
          partner_name: string;
          receipts: number;
          avg_days: number;
          min_days: number;
          max_days: number;
        }>;
      }>(`/api/v1/bi/purchasing/lead-time?${params}&limit=25`),
  });

  return (
    <div className="space-y-6">
      <RangePicker range={range} onChange={setRange} />

      <Card title="Supplier spend" subtitle="By purchase order value.">
        {suppliers.isLoading ? (
          <Loading />
        ) : (
          <Table
            head={['Supplier', 'Code', 'Ordered', 'Qty', 'POs', 'Last order']}
            rows={(suppliers.data?.suppliers ?? []).map((s) => [
              s.partner_name ?? '—',
              s.partner_code ?? '—',
              money(s.amount),
              qty(s.qty),
              String(s.documents),
              s.last_order ?? '—',
            ])}
          />
        )}
      </Card>

      <Card
        title="Lead time"
        subtitle="Days from purchase order to goods received, joined on the document chain. Suppliers with at least 3 receipts."
      >
        {lead.isLoading ? (
          <Loading />
        ) : (lead.data?.suppliers ?? []).length === 0 ? (
          <Empty>
            No chained receipts in this range. Lead time needs GR lines carrying `fromdockey`.
          </Empty>
        ) : (
          <Table
            head={['Supplier', 'Receipts', 'Avg days', 'Fastest', 'Slowest']}
            rows={(lead.data?.suppliers ?? []).map((s) => [
              s.partner_name ?? '—',
              String(s.receipts),
              String(s.avg_days),
              String(s.min_days),
              String(s.max_days),
            ])}
          />
        )}
      </Card>
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

/**
 * Inventory.
 *
 * The caveat banner is not decoration and must not be removed: this tenant exposes no
 * stock-balance endpoint, so these figures are MOVEMENT (received in, delivered out), never stock
 * on hand. A number labelled "on hand" that reconciles with nothing would eventually be used to
 * place a purchase order.
 */
export function BiInventory() {
  const { range, setRange, params } = useRange();
  const [drill, setDrill] = useState<string | null>(null);

  const onHand = useQuery({
    queryKey: ['bi-onhand'],
    queryFn: () =>
      api.get<{
        snapshotDate: string | null;
        note?: string;
        locations: Array<{
          location: string;
          skus: number;
          skus_in_stock: number;
          on_hand: string;
          available: string;
          committed_so: string;
          on_order_po: string;
          negative_skus: number;
        }>;
      }>('/api/v1/bi/inventory/on-hand'),
  });

  const drillItems = useQuery({
    queryKey: ['bi-onhand-items', drill],
    enabled: drill !== null,
    queryFn: () =>
      api.get<{
        items: Array<{
          itemcode: string;
          description: string;
          stockgroup: string | null;
          uom: string | null;
          on_hand: string;
          available: string;
          committed_so: string;
          on_order_po: string;
        }>;
      }>(
        `/api/v1/bi/inventory/on-hand/items?location=${encodeURIComponent(drill ?? '')}&limit=200`,
      ),
  });

  const coverage = useQuery({
    queryKey: ['bi-inv-coverage'],
    queryFn: () =>
      api.get<{
        master: { items: number; active: number; inactive: number; groups_count: number } | null;
        itemsMoved: number;
        groups: Array<{
          stockgroup: string;
          items: number;
          delivered_qty: string;
          delivered_amount: string;
        }>;
        caveat: string;
      }>('/api/v1/bi/inventory/coverage'),
  });

  const movement = useQuery({
    queryKey: ['bi-inv-movement', params],
    queryFn: () =>
      api.get<{
        caveat: string;
        items: Array<{
          itemcode: string;
          description: string | null;
          stockgroup: string | null;
          received: string;
          delivered: string;
          net: string;
          last_delivered: string | null;
          last_received: string | null;
        }>;
      }>(`/api/v1/bi/inventory/movement?${params}&limit=50`),
  });

  const nonMovers = useQuery({
    queryKey: ['bi-inv-nonmovers', params],
    queryFn: () =>
      api.get<{
        caveat: string;
        items: Array<{
          itemcode: string;
          description: string | null;
          stockgroup: string | null;
          received_ever: string;
          last_received: string | null;
          days_since_received: number | null;
        }>;
      }>(`/api/v1/bi/inventory/non-movers?${params}&limit=50`),
  });

  const m = coverage.data?.master;
  const masterEmpty = !m || Number(m.items) === 0;

  return (
    <div className="space-y-6">
      {/* This page is location-first; "how many of THIS item?" is answered one page over, and that
          is not obvious from a sidebar that lists them as separate things. */}
      <div className="rounded border border-slate-200 bg-brand-50 px-4 py-3 text-sm text-slate-700">
        Looking for a particular item?{' '}
        <Link to="/bi/inventory/search" className="font-medium text-brand-700 underline">
          SKU search
        </Link>{' '}
        finds it by code, description or the SKU printed on a tag, and shows the quantity on hand
        across every location.
      </div>

      <Card
        title="Stock on hand by location"
        subtitle={
          onHand.data?.snapshotDate
            ? // A balance snapshot is stamped with a business date, not an instant — no zone to
              // convert, and shifting it would misdate the count it is compared against.
              `SQL Accounting system quantity as at ${businessDate(onHand.data.snapshotDate)}. This is the number a physical count is compared against.`
            : 'Nightly snapshot from SQL Accounting.'
        }
      >
        {onHand.isLoading ? (
          <Loading />
        ) : !onHand.data?.snapshotDate ? (
          <Empty>
            {onHand.data?.note ?? 'No snapshot yet.'} Runs nightly at 02:00, or{' '}
            <code>pnpm etl:balance</code> on demand.
          </Empty>
        ) : (
          <>
            <Table
              head={[
                'Location',
                'On hand',
                'Available',
                'Committed (SO)',
                'On order (PO)',
                'SKUs in stock',
                '',
              ]}
              rows={(onHand.data.locations ?? []).map((l) => [
                l.location,
                cell(Number(l.on_hand), <span className="font-medium">{qty(l.on_hand)}</span>),
                qty(l.available),
                qty(l.committed_so),
                qty(l.on_order_po),
                cell(
                  l.skus_in_stock,
                  <>
                    {l.skus_in_stock} / {l.skus}
                    {l.negative_skus > 0 && (
                      <span
                        className="ml-2 text-alert"
                        title={`${l.negative_skus} SKU(s) carry negative stock in SQL Accounting`}
                      >
                        {l.negative_skus} negative
                      </span>
                    )}
                  </>,
                ),
                <button
                  onClick={() => setDrill(drill === l.location ? null : l.location)}
                  className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-brand-50"
                >
                  {drill === l.location ? 'Hide' : 'Items'}
                </button>,
              ])}
            />
            {drill && (
              <div className="mt-5 border-t border-slate-100 pt-5">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {drill} — items in stock
                </h3>
                {drillItems.isLoading ? (
                  <Loading />
                ) : (
                  <Table
                    head={['Item', 'Description', 'Group', 'On hand', 'Available', 'SO', 'PO']}
                    rows={(drillItems.data?.items ?? []).map((i) => [
                      i.itemcode,
                      i.description,
                      i.stockgroup ?? '—',
                      cell(
                        Number(i.on_hand),
                        <span className="font-medium">{qty(i.on_hand)}</span>,
                      ),
                      qty(i.available),
                      qty(i.committed_so),
                      qty(i.on_order_po),
                    ])}
                  />
                )}
              </div>
            )}
          </>
        )}
      </Card>

      <div className="rounded-lg border border-alert/40 bg-alert/10 px-5 py-3 text-sm text-brand-900">
        <strong className="font-semibold">The panels below are movement, not stock on hand.</strong>{' '}
        They are goods received in and delivery orders out, so they answer &ldquo;what moved&rdquo;,
        not &ldquo;what is there&rdquo; — use the panel above for that. They are still useful:
        movement is what tells you a SKU has stopped selling, which a balance alone cannot.
      </div>

      <RangePicker range={range} onChange={setRange} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label="Items in master"
          value={masterEmpty ? '—' : Number(m.items).toLocaleString()}
          sub={
            masterEmpty ? 'run pnpm etl:stockitems' : `${Number(m.active).toLocaleString()} active`
          }
        />
        <Kpi
          label="Items ever moved"
          value={(coverage.data?.itemsMoved ?? 0).toLocaleString()}
          sub="seen on a GR or DO"
        />
        <Kpi label="Product groups" value={masterEmpty ? '—' : String(m.groups_count)} />
        <Kpi
          label="Non-movers in window"
          value={(nonMovers.data?.items.length ?? 0).toLocaleString()}
          sub="received before, not shipped since"
        />
      </div>

      <Card
        title="Movement by item"
        subtitle="Received minus delivered, in the selected window. Ranked by the size of the swing."
      >
        {movement.isLoading ? (
          <Loading />
        ) : (
          <Table
            head={['Item', 'Description', 'Group', 'Received', 'Delivered', 'Net', 'Last shipped']}
            rows={(movement.data?.items ?? []).map((i) => [
              i.itemcode,
              i.description ?? '—',
              i.stockgroup ?? '—',
              qty(i.received),
              qty(i.delivered),
              <span className={Number(i.net) < 0 ? 'text-alert' : 'text-brand-700'}>
                {qty(i.net)}
              </span>,
              i.last_delivered ?? 'never',
            ])}
          />
        )}
      </Card>

      <Card
        title="Non-movers"
        subtitle="Received at some point, but with no delivery order in the selected window. Quantity is total ever received."
      >
        {nonMovers.isLoading ? (
          <Loading />
        ) : (
          <Table
            head={['Item', 'Description', 'Group', 'Received ever', 'Last received', 'Days since']}
            rows={(nonMovers.data?.items ?? []).map((i) => [
              i.itemcode,
              i.description ?? '—',
              i.stockgroup ?? '—',
              qty(i.received_ever),
              i.last_received ?? '—',
              i.days_since_received === null ? (
                '—'
              ) : (
                <span className={Number(i.days_since_received) > 365 ? 'text-alert' : ''}>
                  {i.days_since_received}
                </span>
              ),
            ])}
          />
        )}
      </Card>

      <Card title="Product groups" subtitle="Catalogue size and delivered value by stock group.">
        {coverage.isLoading ? (
          <Loading />
        ) : masterEmpty ? (
          <Empty>
            The item master mirror is empty. Run <code>pnpm etl:stockitems</code> — movement figures
            above are unaffected, only descriptions and groups are missing.
          </Empty>
        ) : (
          <Table
            head={['Group', 'Items', 'Delivered qty', 'Delivered value']}
            rows={(coverage.data?.groups ?? []).map((g) => [
              g.stockgroup,
              String(g.items),
              qty(g.delivered_qty),
              money(g.delivered_amount),
            ])}
          />
        )}
      </Card>
    </div>
  );
}
